import { useCallback, useEffect, useRef, useState } from 'react';
import { useInterval } from '@tenant/utility/hooks/useInterval';
import { getPipelineRunLogQuery } from '../apis';
import {
  PIPELINE_RUN_LOG_POLL_INTERVAL_MS,
  PipelineRunStatus,
} from '../constants/pipelineRunStatus';

// deploy-pipelines-run-logs (Wave 3) — the build log's own fetcher. Reuses the house
// `useInterval` (`delay = null` pauses it) exactly as usePollPipelineRun does, so the
// stop condition is a single readable expression rather than buried in react-query options.
//
// ─────────────────────────────────────────────────────────────────────────────────────────
// THIS HOOK DOES NOT POLL THE RUN. It calls `getPipelineRunLogQuery` and nothing else — there
// is no `getPipelineRunQuery` import above and there must never be one (AC-9). Wave 1's AC-17
// pins ONE run-status poller per page and PipelineRunStatusView owns it; the run's `status` is
// handed to this hook as a PROP, driven by that existing poll. What this hook owns is a
// different resource (the log) on a different, slower cadence — not a duplicate of the run
// poller. A reviewer greps this file for `getPipelineRunQuery`; it should find only this
// sentence.
// ─────────────────────────────────────────────────────────────────────────────────────────
//
// Fetch triggers, exhaustively:
//   1. Once on mount.
//   2. Once whenever the displayed run changes (pipelineId / runId).
//   3. Once whenever `status` ENTERS or LEAVES Building — free, no timer, no extra run request;
//      it rides the value the page is already polling. NOT on every status change: see the
//      guard on the effect below, and AC-7.
//   4. While `status === Building` ONLY, additionally every PIPELINE_RUN_LOG_POLL_INTERVAL_MS.
//
// Stop condition (AC-7) — a HARD stop, not a slower poll. Once `status !== Building` the
// interval's delay is null and zero further log requests are issued for that run, ever. This
// is safe rather than merely cheap for the ACR-terminal path: `DockerRepositoryHostedService`
// writes `Logs` and `Status` in the SAME `ExecuteUpdateAsync`, so the final log is committed by
// the very statement that makes the build terminal. Trigger 3 fires exactly once on the way out
// of Building and there is nothing left to fetch afterwards. A run that is ALREADY terminal
// when the page loads therefore fetches exactly once, on mount.
//
// THE ONE EXCEPTION, and it is recorded rather than handled here (spec `## Risks`, follow-up
// `deploy-pipelines-run-log-timeout-tail`): `PipelineRunHostedService.AdvanceBuildingAsync`
// also fails a run on a BUILD TIMEOUT while the ACR image is still Queued/Started/Running.
// Nothing cancels the ACR run, so `Logs` keeps growing for minutes after this hook has stopped.
// On that path the reader sees a mid-build fragment and must reload the page. Deliberately not
// special-cased in this wave — a bounded post-timeout poll is its own slice.
//
// AC-13 — a failed fetch is non-blocking and NEVER destructive: `isError` is raised and the
// last successfully-fetched log stays in state untouched. There is no page-level error path
// out of here.

// Guid casing is not guaranteed to match between the value the page holds and the value the
// server echoes, and a case-sensitive mismatch here would discard every response.
const isSameRunId = (a, b) =>
  !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

const useGetPipelineRunLog = ({ pipelineId, runId, status, enabled = true }) => {
  const [log, setLog] = useState(null);
  const [isLoading, setIsLoading] = useState(!!runId);
  const [isError, setIsError] = useState(false);

  // AC-10 — the late-response guard. Assigned during render (not in an effect) so it is
  // already correct by the time any effect in this commit fires a request; it is only ever
  // read from async callbacks, never during render, which is what makes that safe.
  const currentRunIdRef = useRef(runId);
  currentRunIdRef.current = runId;

  const fetchOnce = useCallback(async () => {
    if (!pipelineId || !runId) {
      return;
    }
    const requestedRunId = runId;
    setIsLoading(true);
    try {
      const { data } = await getPipelineRunLogQuery({ pipelineId, runId });
      // The response echoes the run it belongs to precisely so a slow reply for run A cannot
      // paint over run B. Fall back to the id we asked for if the echo is absent.
      if (!isSameRunId(data?.RunId ?? requestedRunId, currentRunIdRef.current)) {
        return;
      }
      setLog(data);
      setIsError(false);
    } catch (error) {
      if (!isSameRunId(requestedRunId, currentRunIdRef.current)) {
        return;
      }
      // Deliberately does NOT clear `log` (AC-13). A dropped poll must not blank a log the
      // reader is part-way through; the panel shows an inline notice above the stale copy.
      setIsError(true);
    } finally {
      if (isSameRunId(requestedRunId, currentRunIdRef.current)) {
        setIsLoading(false);
      }
    }
  }, [pipelineId, runId]);

  // The status this hook last ACTED on for the current run. `undefined` means "nothing fetched
  // for this run yet", which is what makes the mount and run-change fetches fall out of the same
  // expression as the transition fetch below. Reset in the wipe effect, which is declared first
  // and therefore runs first in the same commit.
  const actedOnStatusRef = useRef(undefined);

  // Trigger 2 — a different run is a different log. This is the ONE place state is wiped, and
  // it is not the AC-13 case: switching runs SHOULD clear, a failed fetch should not.
  useEffect(() => {
    setLog(null);
    setIsError(false);
    setIsLoading(!!runId);
    actedOnStatusRef.current = undefined;
  }, [pipelineId, runId]);

  // Triggers 1, 2 and 3.
  //
  // A status change alone is NOT enough to justify a request. The production lifecycle is
  // Queued -> Building -> Deploying -> Succeeded, so an unguarded `status` dependency fires on
  // Deploying -> Succeeded too — a request issued while the run is already non-Building, which
  // AC-7 forbids in as many words, and which fetches a log that provably cannot have changed
  // since the build went terminal. The guard below is the whole of AC-7/AC-8's contract:
  //
  //   • first observation of this run (mount, or a run change)   -> fetch
  //   • ENTERING Building (Queued -> Building)                   -> fetch, the log now exists
  //   • LEAVING Building (-> Deploying / Succeeded / Failed)     -> fetch, exactly once, final
  //   • any other transition (Deploying -> Succeeded, and so on) -> NOTHING
  useEffect(() => {
    if (!enabled || !pipelineId || !runId) {
      return;
    }

    const actedOnStatus = actedOnStatusRef.current;
    const isFirstForThisRun = actedOnStatus === undefined;
    const wasBuilding = actedOnStatus === PipelineRunStatus.Building;
    const isBuilding = status === PipelineRunStatus.Building;

    if (!isFirstForThisRun && !wasBuilding && !isBuilding) {
      return;
    }

    actedOnStatusRef.current = status;
    fetchOnce();
  }, [enabled, pipelineId, runId, status, fetchOnce]);

  // Trigger 4 / AC-7. `delay = null` is the hard stop, and it is also the teardown: useInterval
  // clears its timer on unmount and whenever the delay changes.
  //
  // Note on "clear on run change": when the run changes, `delay` is unchanged if BOTH runs are
  // Building, so the same interval keeps ticking — but it now invokes the new `fetchOnce`,
  // targeting the new run, which is the wanted behaviour. Nothing from the old run can be
  // applied: every response passes the currentRunIdRef check above. If the new run is not
  // Building the delay flips to null and the timer is torn down outright.
  const shouldPoll =
    enabled && !!pipelineId && !!runId && status === PipelineRunStatus.Building;

  useInterval(fetchOnce, shouldPoll ? PIPELINE_RUN_LOG_POLL_INTERVAL_MS : null);

  return { log, isLoading, isError, refetch: fetchOnce };
};

export default useGetPipelineRunLog;
